<template>
  <component :is="props.tag" ref="reference" @click="onClick" :class="classList">
    <teleport to="body">
      <floating-transition key="floating-transition">
        <template v-if="isOpened">
          <div
            :class="popupClassList"
            ref="floating"
            :style="{
              top: `${y ?? 0}px`,
              left: `${x ?? 0}px`,
              zIndex: backdropStore.zIndex,
            }"
            :data-floating-placement="placement"
            key="floating-detached"
            @click="closeOnPopupClick"
          >
            <template v-if="$slots.popup">
              <slot name="popup"/>
            </template>
          </div>
        </template>
      </floating-transition>
    </teleport>
    <slot/>

    <backdrop
      :model-value="isOpened"
      @close="close"
    />
  </component>
</template>

<script setup>
  import FloatingTransition from './floating-transition.vue';
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import { autoUpdate, flip, shift, useFloating } from '@floating-ui/vue';
  import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
  import Backdrop from '@/components/backdrop/backdrop.vue';
  import { useBackdropStore } from '@/stores/backdrop';

  onBeforeMount(() => {
    if (typeof window !== 'undefined') {
      window.ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
    }
  });

  const emit = defineEmits(['closed', 'opened']);

  const props = defineProps({
    tag: {
      type: String,
      default: 'div',
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    popupClass: {
      type: null,
      default: null,
    },
    openClass: {
      type: String,
      default: 'floating--open',
    },
    placement: {
      type: null,
      default: 'bottom-start',
    },
    disabled: {
      type: Boolean,
    },
    animationFrame: {
      type: Boolean,
      default: false,
    },
    hideOnClick: {
      type: Boolean,
      default: false,
    },
    hideOnPopupClick: {
      type: Boolean,
      default: true,
    },
    ignoreSelectedTextCheck: {
      type: Boolean,
      default: false,
    },
  });

  const reference = ref(null);
  const floating = ref(null);
  const isOpened = ref(props.modelValue);
  const backdropStore = useBackdropStore();
  const GAP_FROM_EDGE = 10;

  const { x, y } = useFloating(reference, floating, {
    placement: props.placement,
    middleware: [flip(), shift({ padding: GAP_FROM_EDGE })],
    whileElementsMounted(...args) {
      return autoUpdate(...args, { animationFrame: props.animationFrame });
    },
  });

  watch(() => props.modelValue, (newValue) => {
    if (newValue === false) {
      close();
    }
  });

  const classList = computed(() => {
    const list = ['floating-wrapper'];

    if (isOpened.value) {
      list.push(props.openClass);
    }

    return list;
  });

  const popupClassList = computed(() => {
    const list = ['floating'];

    if (Array.isArray(props.popupClass)) {
      return list.concat(props.popupClass);
    } else if (props.popupClass) {
      list.push(props.popupClass);
    }

    return list;
  });

  function onClick(event) {
    if (props.hideOnClick) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    toggle();
  }

  function toggle() {
    if (isOpened.value) {
      close();
    } else {
      open();
    }
  }

  function close() {
    // check if any text is highlighted
    const selection = window.getSelection().toString();

    if (selection && !props.ignoreSelectedTextCheck) {
      return;
    }

    if (!isOpened.value) {
      return;
    }

    isOpened.value = false;
    emit('closed');
  }

  function closeOnPopupClick() {
    if (!props.hideOnPopupClick) {
      return;
    }

    close();
  }

  function open() {
    if (isOpened.value || props.disabled) {
      return;
    }

    isOpened.value = true;
    emit('opened');
  }

  defineExpose({ floating });
</script>

